<template>
  <div>
    <div class="calculator-container" :class="{ 'remove-margin': removeMargin }">
      <h1 class="h3">
        Skewness Calculator
      </h1>
      <div class="label-tooltip your-data-tooltip">
        <p class="your-data">
          Your Data
        </p>
        <div class="icon-info-tooltip">
          <IconInfo />
          <Tooltip>
            <span>Select <strong>Population</strong> if the data contains all measurable values or all values you are interested in.<br>
              Select <strong>Sample</strong> if the data is a sample of a large or unlimited population and you wish to make a statement about the entire population.</span>
          </Tooltip>
        </div>
      </div>
      <Dropdown
        class="calculation-method"
        :reset="resetProp"
        calculator
        selected-title="Sample"
        :selected="selectedCalculationMethod"
        :radio-buttons="{ sample: 'Sample', population: 'Population' }"
        @change="currentSelectedCalculationMethod"
      />
      <form @submit.prevent="calculations">
        <div class="raw-data">
          <div class="label-tooltip">
            <label class="type-data">
              Enter Data
            </label>
            <div class="icon-info-tooltip skewness">
              <IconInfo />
              <Tooltip>
                <span>Input the data separated by comma, space or enter. To calculate skewness, you need at least three values in your dataset.</span>
              </Tooltip>
            </div>
          </div>
          <div class="textarea-border" :class="{ 'invalid-value': textareaAlert }">
            <textarea
              v-model="rawDataMean"
              cols="30" rows="10"
              placeholder="7,1,6,2,11

7 1 6 2 11

7
1
6"
            />
          </div>
          <div v-if="textareaAlert" class="error">
            {{ textareaAlert }}
          </div>
        </div>
        <div class="decimal-places">
          <div class="label-tooltip">
            <label>
              Decimal Places
            </label>
            <div class="icon-info-tooltip skewness">
              <IconInfo />
              <Tooltip>
                <span>Choose the number of decimal places.</span>
              </Tooltip>
            </div>
          </div>
          <Dropdown
            calculator
            calculator-decimal-places
            class="small-dropdown"
            :selected="selectedDecimalPlaces"
            :radio-buttons="{ 1: '1', 2: '2', 3: '3', 4: '4', 5: '5', 6: '6', 7: '7', 8: '8', 9: '9' }"
            @change="currentSelectedDecimalPlace"
          />
        </div>
        <div class="buttons">
          <button class="btn-primary calculate">
            Calculate
          </button><button
            class="btn-primary-light reset"
            @click="reset"
          >
            Reset
          </button>
          <button class="load-example btn-link-accent" @click="loadExample">
            Load Example
          </button>
        </div>
      </form>
    </div>
    <div
      v-show="skewness" id="summary" class="summary-container"
    >
      <h4>Summary:</h4>
      <div class="results">
        <div>
          <span class="result-name">Skewness:</span> <span class="result-value">{{ skewness }}</span>
        </div>
      </div>
    </div>
    <div v-if="skewness && skewness !== 'NaN'" class="interpretation-container">
      <h4>Interpretation:</h4>
      <p v-html="currentInterpretationText()" />
    </div>
  </div>
</template>

<script>
import Dropdown from '@itbpbg/lms-components/src/components/Dropdown.vue';
import Tooltip from '@itbpbg/lms-components/src/components/Tooltip.vue';
import IconInfo from '@itbpbg/lms-components/src/assets/svg/icon-info.svg?vue-component';

export default {
  components: {
    Dropdown, Tooltip, IconInfo,
  },
  data() {
    return {
      skewness: '',
      rawDataMeanArray: [],
      rawDataMean: '',
      selectedCalculationMethod: 'sample',
      textareaAlert: false,
      selectedDecimalPlaces: '4',
      removeMargin: false,
      resetProp: false,
      resultNumber: 0,
    };
  },
  computed: {
    hideResultVariablesArray() {
      return [
        this.rawDataMean,
        this.selectedDecimalPlaces,
      ];
    },
  },
  watch: {
    selectedCalculationMethod() {
      this.defaultingInput();
    },
    hideResultVariablesArray() {
      this.hideResults();
    },
  },
  mounted() {
    this.$store.commit('calculators/result', false);
  },
  methods: {
    currentSelectedCalculationMethod(currentMethod) {
      this.selectedCalculationMethod = currentMethod;
    },
    async scrollToSection() {
      this.$nextTick(() => {
        let yOffset;
        if (this.$store.state.layout === 'desktop') {
          yOffset = -90;
        } else {
          yOffset = this.$config.project === 'lms' ? -64 : -68;
        }

        const section = 'summary';
        const element = document.querySelector(`#${section}`);
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

        setTimeout(() => {
          this.clickedSection = section;
          window.scrollTo({ top: y, behavior: 'smooth' });

          setTimeout(() => {
            this.clickedSection = null;
          }, 500);
        }, 200);
      });
    },
    reset(e) {
      this.selectedCalculationMethod = 'sample';
      this.defaultingInput();
      e.preventDefault();
    },
    defaultingInput() {
      this.resetProp = true;
      this.rawDataMean = '';
      this.selectedDecimalPlaces = '4';
      this.skewness = '';
      this.$store.commit('calculators/result', false);
      this.textareaAlert = '';
    },
    hideResults() {
      this.removeMargin = false;
      this.skewness = '';
      this.$store.commit('calculators/result', false);
      this.textareaAlert = '';
    },
    loadExample(e) {
      this.textareaAlert = '';
      this.selectedDecimalPlaces = '4';
      this.removeMargin = false;
      this.rawDataMean = '6\n7\n5\n7\n5\n4\n4\n6\n1\n3\n2\n8';
      this.skewness = '';
      this.$store.commit('calculators/result', false);

      e.preventDefault();
    },
    currentSelectedDecimalPlace(selectedDecimalPlaces) {
      this.selectedDecimalPlaces = selectedDecimalPlaces;
    },
    inputValidation() {
      this.textareaAlert = '';
      this.rawDataMeanArray = this.rawDataMean.split(/,|\s+|\n+/).filter((value) => value !== '');

      if (this.rawDataMeanArray.length === 0) {
        this.textareaAlert = 'Field required';
        this.rawDataMean = '';
        return false;
      }

      for (let i = 0; i < this.rawDataMeanArray.length; i += 1) {
        if (this.rawDataMeanArray[i] < 0 || Number.isNaN(Number(this.rawDataMeanArray[i]))) {
          this.textareaAlert = 'Invalid value';
          return false;
        }
      }

      if (this.rawDataMeanArray.length < 3 && this.rawDataMeanArray.length > 0) {
        this.textareaAlert = 'To calculate skewness, you need at least three values in your dataset';

        return false;
      }

      return true;
    },
    calculations() {
      if (!this.inputValidation()) {
        return;
      }

      let mean = 0;
      let size = 0;
      let standardDeviation = 0;
      const standardizationsArray = [];
      let cubedStandardizationsArray = [];
      let cubedStandardizationsValuesSum = 0;
      let skewness = 0;

      // Calculating the mean
      for (let i = 0; i < this.rawDataMeanArray.length; i += 1) {
        mean += Number(this.rawDataMeanArray[i]);
      }

      mean /= this.rawDataMeanArray.length;

      // Get the size
      size = this.rawDataMeanArray.length;

      // Calculating sample/population standard deviation
      for (let i = 0; i < this.rawDataMeanArray.length; i += 1) {
        standardDeviation += (Number(this.rawDataMeanArray[i]) - mean) ** 2;
      }

      if (this.selectedCalculationMethod === 'sample') {
        standardDeviation = Math.sqrt(standardDeviation / (size - 1));
      } else {
        standardDeviation = Math.sqrt(standardDeviation / size);
      }

      // Standardization
      for (let i = 0; i < this.rawDataMeanArray.length; i += 1) {
        standardizationsArray.push((this.rawDataMeanArray[i] - mean) / standardDeviation);
      }

      // Cube standardized values
      cubedStandardizationsArray = standardizationsArray.map((num) => num ** 3);

      // Calculate cubed standardized values sum
      cubedStandardizationsValuesSum = cubedStandardizationsArray.reduce((a, b) => a + b, 0);

      // Calculate sample/population skewness
      if (this.selectedCalculationMethod === 'sample') {
        skewness = (size / ((size - 1) * (size - 2))) * cubedStandardizationsValuesSum;
      } else {
        skewness = (1 / size) * cubedStandardizationsValuesSum;
      }

      // Formatting result
      this.skewness = skewness.toFixed(this.selectedDecimalPlaces);

      const onlyZerosTest = /^\d+.[0]+$/;
      if (onlyZerosTest.test(this.skewness)) {
        this.skewness = Math.trunc(this.skewness).toString();
      }

      this.removeMargin = true;
      this.$store.commit('calculators/result', this.resultNumber += 1);
      this.calculationData(mean, size, standardDeviation, cubedStandardizationsValuesSum);
      this.scrollToSection();
    },
    calculationData(mean, size, standardDeviation, cubedStandardizationsValuesSum) {
      this.$store.commit('calculators/calculationData',
        {
          method: this.selectedCalculationMethod,
          rawDataMeanArray: this.rawDataMeanArray,
          mean,
          size,
          standardDeviation,
          selectedDecimalPlaces: this.selectedDecimalPlaces,
          cubedStandardizationsValuesSum: cubedStandardizationsValuesSum.toFixed(this.selectedDecimalPlaces),
          skewness: this.skewness,
        });
    },
    currentInterpretationText() {
      let currentText = '';
      const skewness = Number(this.skewness);

      if (skewness === 0) {
        currentText = '<span>There is no <span class="colored-texts">skewness</span>-indicating that the data is perfectly symmetric.</span>';
      } else if (skewness > 0 && this.skewness <= 0.5) {
        currentText = '<span>There is a <span class="colored-texts">weak positive skewness</span>-indicating that the data is approximately symmetric, with a slight skew to the <span class="colored-texts">right</span>.</span>';
      } else if (skewness > -0.5 && this.skewness <= 0) {
        currentText = '<span>There is a <span class="colored-texts">weak negative skewness</span>-indicating that the data is approximately symmetric, with a slight skew to the <span class="colored-texts">left</span>.</span>';
      } else if (skewness > 0.5 && this.skewness <= 1) {
        currentText = '<span>There is a <span class="colored-texts">moderate positive skewness</span>-indicating that the data is moderately skewed to the <span class="colored-texts">right</span>.</span>';
      } else if (skewness >= -1 && this.skewness < -0.5) {
        currentText = '<span>There is a <span class="colored-texts">moderate negative skewness</span>-indicating that the data is moderately skewed to the <span class="colored-texts">left</span>.</span>';
      } else if (skewness > 1) {
        currentText = '<span>Your dataset has <span class="colored-texts">high positive skewness</span>—indicating that the data is highly skewed to the <span class="colored-texts">right</span>.</span>';
      } else if (skewness < -1) {
        currentText = '<span>Your dataset has <span class="colored-texts">high negative skewness</span>—indicating that the data is highly skewed to the <span class="colored-texts">left</span>.</span>';
      }

      return currentText;
    },
  },
};
</script>

<style lang="scss" scoped>
.calculator-container {
  .decimal-places {
    width: calc(50% - 15px);
    margin-top: var(--spacing-30);
    margin-bottom: var(--spacing-36);

    .dropdown::v-deep {
      &.open {
        .list {
          width: 100.63%;
          left: -1px;
        }
      }
    }
  }

  .raw-data {
    .label-tooltip .icon-info-tooltip {
      &.skewness {
        &:hover {
          .tooltip {
            height: auto;
          }
        }
      }
    }
  }
}

.summary-container {
  min-height: unset !important;

  .results {
    min-height: unset !important;

    div {
      justify-content: space-between;
      padding: 0 15px 0 10px;
    }
  }
}

.interpretation-container {
  min-height: unset !important;
  height: 172px;

  p {
    ::v-deep .colored-texts {
      font-weight: 600;
      color: var(--accent-shade-3);
    }
  }
}

@include bp($bp-mobile) {
  .calculator-container {
    &.remove-margin {
      margin-bottom: 0;
    }

    .decimal-places {
      .label-tooltip .icon-info-tooltip {
        &.skewness {
          &:hover {
            .tooltip:after {
              left: 49.7%;
            }
          }
        }
      }
    }

    .raw-data {
      .label-tooltip .icon-info-tooltip {
        &.skewness {
          &:hover {
            .tooltip {
              left: 130%;

              &:after {
                left: 42.15%;
              }
            }
          }
        }
      }
    }
  }

  div .summary-container {
    margin-top: var(--spacing-60);
  }
}

@include bp($bp-phone) {
  .calculator-container {
    .decimal-places {
      width: 100%;

      .dropdown::v-deep {
        &.open {
          .list {
            top: 101.9%;
          }
        }
      }

      .label-tooltip .icon-info-tooltip {
        &.skewness {
          &:hover {
            .tooltip:after {
              left: 46%;
            }
          }
        }
      }
    }

    .raw-data {
      .label-tooltip .icon-info-tooltip {
        &.skewness {
          &:hover {
            .tooltip {
              left: 271%;

              &:after {
                left: 34.5%;
              }
            }
          }
        }
      }
    }
  }

  div .interpretation-container {
    padding: var(--spacing-32) var(--spacing-20);
  }
}

@import '../../../../assets/sass/ds/calculators/calculators.scss';
</style>
